import { formatNumberWithCommaAndCurrency } from './utils';

const { isPaymentRequest, isEstimate, calculateMaxAmount } = require('./utils');
import { PAYMENT_MAP } from 'types/constants';
import { getCurrencySymbol } from '../businessLogic/server/scsToIcnAdapter';
import { saleSelectors } from '../store/sale/selectors';

const { AMOUNT_CONFIG } = PAYMENT_MAP;

const ScsWalletType = {
  BANK: 'BANKACCOUNT',
  CARD: 'CARD',
};

const ScsPaymentType = {
  CREDIT_CARD_WALLET: 'credit card',
  BANK_ACCOUNT_WALLET: 'bank',
};

export const isCreditCardPaymentMethod = (paymentMethodType) =>
  paymentMethodType === 'cc' || paymentMethodType === 'dc' || paymentMethodType === 'dc,cc';
export const isBankAccountPaymentMethod = (paymentMethodType) =>
  paymentMethodType === 'bank' || paymentMethodType === 'eft';
export const isNanopayAccountPaymentMethod = (paymentMethodType) => paymentMethodType === 'nanopay';
export const isApplePayPaymentMethod = (paymentMethodType) => paymentMethodType === 'ap';
export const isPayPalPaymentMethod = (paymentMethodType) => paymentMethodType === 'pp';
export const isNanopayPaymentMethod = (paymentMethodType) => paymentMethodType === 'nanopay';

/**
 * Canonical mapping from payment method identifiers to human-readable display labels.
 * Covers both `paymentMethodType` values (e.g. 'pp', 'paypal_ppaam') and
 * API response `paymentMethod` values (e.g. 'PayPal', 'PayPalCommerce').
 *
 * Card and bank types are intentionally omitted because they require
 * masked-number formatting that varies by context.
 */
const PAYMENT_METHOD_DISPLAY_LABELS = {
  // PayPal variants
  pp: 'PayPal',
  paypal_ppaam: 'PayPal',
  PayPal: 'PayPal',
  PayPalCommerce: 'PayPal',
  // Venmo
  venmo: 'Venmo',
  Venmo: 'Venmo',
  // Apple Pay
  ap: 'Apple Pay',
  // Pay by Bank
  pbb: 'Pay by Bank',
  // Buy Now Pay Later
  affirm_bnpl: 'Affirm',
};

/**
 * Resolves a human-readable display label for a payment method.
 * The API response value takes precedence over the local type, since
 * the server's answer is the most authoritative source.
 *
 * Returns undefined for card/bank types that need masked-number formatting.
 *
 * @param {string} [paymentMethodType] - Local payment method type (e.g. 'pp', 'paypal_ppaam', 'ap')
 * @param {string} [responsePaymentMethod] - paymentMethod value from the API response
 * @returns {string|undefined}
 */
export const getPaymentMethodDisplayLabel = (paymentMethodType, responsePaymentMethod) =>
  PAYMENT_METHOD_DISPLAY_LABELS[responsePaymentMethod] ??
  PAYMENT_METHOD_DISPLAY_LABELS[paymentMethodType];

const COF_ELIGIBLE_PAYMENT_METHODS = ['cc', 'dc', 'bank'];
export const isCOFEligiblePaymentMethod = (paymentMethodType) =>
  COF_ELIGIBLE_PAYMENT_METHODS.includes(paymentMethodType);

export const isAffirmBnplAmountEligible = (amount, affirmMinTxnLimit, affirmMaxTxnLimit) =>
  amount >= affirmMinTxnLimit && amount <= affirmMaxTxnLimit;

export const PaymentAmountValidator = {
  getMinPaymentAmount: (
    balanceAmount,
    paymentMethod,
    isEstimatePayment,
    amountConfig,
    affirmMinTxnLimit
  ) => {
    // For estimate flows, min is the balance due (deposit cannot be less than balance).
    if (isEstimatePayment) {
      return Number.parseFloat(balanceAmount);
    }
    // BNPL (Affirm) uses its own min/max transaction limits.
    if (paymentMethod === 'affirm_bnpl') {
      return affirmMinTxnLimit;
    }
    return amountConfig?.minPaymentAmount;
  },
  getMaxPaymentAmount: (
    balanceAmount,
    paymentMethod,
    allowOverPay,
    region,
    featureFlags,
    amountConfig,
    affirmMaxTxnLimit
  ) => {
    let maxAmount;
    const greaterMaxAmountPaymentMethods = ['bank', 'cc', 'dc', 'dc,cc', 'ap'];
    if (
      region === 'US' &&
      featureFlags &&
      (featureFlags['cp-increase-max-amount-limit'] ||
        featureFlags['SBSEG-CP-high-value-payments-enabled']) &&
      paymentMethod &&
      greaterMaxAmountPaymentMethods.includes(paymentMethod)
    ) {
      maxAmount = calculateMaxAmount(
        balanceAmount,
        amountConfig?.multipleFactor,
        amountConfig?.maxAllowedAmountCcDcACH
      );
    } else if (paymentMethod === 'affirm_bnpl') {
      maxAmount = calculateMaxAmount(
        balanceAmount,
        amountConfig?.multipleFactor,
        affirmMaxTxnLimit
      );
    } else if (paymentMethod === 'eft') {
      maxAmount = calculateMaxAmount(
        balanceAmount,
        amountConfig?.multipleFactor,
        amountConfig?.maxEftAllowedAmount
      );
    } else {
      maxAmount = calculateMaxAmount(
        balanceAmount,
        amountConfig?.multipleFactor,
        amountConfig?.maxAllowedAmount
      );
    }

    //In case this invoice cannot be over-payed then we should return balanceAmount as max amount or less
    if (maxAmount > balanceAmount && !allowOverPay) {
      return balanceAmount;
    }
    return maxAmount;
  },
  getOpenInvoiceMaxPaymentAmount: (balanceAmount, achPaymentEnabled, creditCardPaymentEnabled) => {
    if (achPaymentEnabled) {
      return AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT_CC_DC_ACH;
    } else if (creditCardPaymentEnabled) {
      return AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT;
    }
    return balanceAmount;
  },
  validate: ({
    amount,
    balanceAmount,
    paymentMethod,
    allowOverPay,
    transactionType,
    gratuityValue = 0,
    region,
    featureFlags,
    saleAmount,
    amountConfig,
    isBatchPayments,
    affirmMinTxnLimit,
    affirmMaxTxnLimit,
  }) => {
    if (isPaymentRequest(transactionType)) return true;

    if (!amount || !balanceAmount) {
      return false;
    }

    // Skip validation for batch payments feature when amount > 0 and not default invoice only
    // This allows paying non-default invoices or multiple invoices without validation errors
    // Note: isBatchPayments is already computed using isBatchPaymentsEnabled selector
    // which checks both feature flag AND experiment treatment, so no need to check FF again
    if (isBatchPayments && amount > 0) {
      return true;
    }

    if (isEstimate(transactionType)) {
      return amount >= balanceAmount && amount <= saleAmount;
    }

    const max = PaymentAmountValidator.getMaxPaymentAmount(
      balanceAmount,
      paymentMethod,
      allowOverPay,
      region,
      featureFlags,
      amountConfig,
      affirmMaxTxnLimit
    );
    const greaterMaxAmountPaymentMethods = ['bank', 'cc', 'dc', 'dc,cc', 'ap'];
    const shouldIncreaseMaxAllowed =
      greaterMaxAmountPaymentMethods.includes(paymentMethod) &&
      region === 'US' &&
      featureFlags &&
      (featureFlags['cp-increase-max-amount-limit'] ||
        featureFlags['SBSEG-CP-high-value-payments-enabled']);
    const maxAllowed = shouldIncreaseMaxAllowed
      ? amountConfig?.maxAllowedAmountCcDcACH
      : amountConfig?.maxAllowedAmount;

    const totalAmount = amount + gratuityValue;
    if (paymentMethod === 'affirm_bnpl') {
      return (
        max >= amount &&
        isAffirmBnplAmountEligible(totalAmount, affirmMinTxnLimit, affirmMaxTxnLimit)
      );
    }

    return (
      max >= amount && totalAmount >= amountConfig?.minPaymentAmount && totalAmount <= maxAllowed
    );
  },
  validateGratuity: ({ amount, gratuityAmount }) => {
    return !(
      (amount < 5 && gratuityAmount > 5) ||
      (amount >= 5 && amount < 200 && gratuityAmount > amount) ||
      (amount >= 200 && gratuityAmount > amount * 0.5) ||
      gratuityAmount > 1000
    );
  },
  getCanBeFullyPaid(balanceAmount, enabledPaymentMethods, region, featureFlags = {}, amountConfig) {
    let canBeFullyPaid = false;
    const isBankOrCardEnabled =
      enabledPaymentMethods.includes('cc') || enabledPaymentMethods.includes('bank');
    const isHighValueEnabled =
      featureFlags?.['cp-increase-max-amount-limit'] ||
      featureFlags?.['SBSEG-CP-high-value-payments-enabled'];
    if (
      region === 'US' &&
      isHighValueEnabled &&
      isBankOrCardEnabled &&
      balanceAmount <= amountConfig?.maxAllowedAmountCcDcACH
    ) {
      canBeFullyPaid = true;
    } else if (
      (region !== 'US' || !isHighValueEnabled) &&
      isBankOrCardEnabled &&
      balanceAmount <= amountConfig?.maxAllowedAmount
    ) {
      canBeFullyPaid = true;
    }
    return canBeFullyPaid;
  },
  isPartialPaymentEnabled: (feeAmount, partialPaymentEnabled, transactionType, featureFlags) => {
    if (isEstimate(transactionType)) {
      return !!featureFlags?.['cp-allow-deposit-amount-edit'];
    }
    return feeAmount !== undefined ? false : partialPaymentEnabled;
  },
};

// This function get masked account number and format it according to this pattern ****[LAST_FOUR_DIGITS]
export const formatMaskedAccountNumber = (maskedAccountNumber) => {
  const accountNumberPrefix = maskedAccountNumber.slice(0, maskedAccountNumber.length - 4);
  const accountNumberLastFourDigits = maskedAccountNumber.slice(maskedAccountNumber.length - 4);
  return `${accountNumberPrefix.replace(/./g, '*')}${accountNumberLastFourDigits}`;
};

// This function get masked card number and format it according to this pattern [CARD_TYPE]****[LAST_FOUR_DIGITS]
export const formatMaskedCardNumber = (cardType, maskedCardNumber) => {
  if (cardType === 'american-express') {
    cardType = 'amex';
  }
  const cardTypeConverted = cardType.toUpperCase();
  const cardNumberLastFourDigits = maskedCardNumber.slice(maskedCardNumber.length - 4);
  return `${cardTypeConverted}****${cardNumberLastFourDigits}`;
};

/**
 * Normalizes a raw masked card string:
 *  - collapses whitespace runs to a single space
 *  - replaces XXX (any case) with ***
 */
const normalizeMaskedCard = (raw) =>
  String(raw).replace(/\s+/g, ' ').replace(/XXX/gi, '***').trim();

/**
 * Ensures bank payment strings are prefixed with "Bank".
 */
const withBankPrefix = (normalized) =>
  normalized.toUpperCase().startsWith('BANK') ? normalized : `Bank ${normalized}`;

/**
 * Formats payment method display string for success screens.
 * Handles branded methods (PayPal, Venmo, etc.), masked card numbers, and fallback scenarios.
 *
 * @param {Object} params - Payment formatting parameters
 * @param {string} params.paymentMethodType - Type of payment method ('bank', 'cc', 'dc', etc.)
 * @param {Object} [params.lastSuccessfulPaymentResponse] - Response from regular payment
 * @param {Object} [params.lastSuccessfulSchedulePaymentResponse] - Response from scheduled payment
 * @returns {string} Formatted payment method string (e.g., "PayPal", "VISA***1234", "Bank PERSONAL_CHECKING ***0278")
 */
export const formatPaymentMethodDisplay = ({
  paymentMethodType,
  lastSuccessfulPaymentResponse,
  lastSuccessfulSchedulePaymentResponse,
}) => {
  // Branded payment methods resolve to a simple label — no masked numbers needed
  const label = getPaymentMethodDisplayLabel(
    paymentMethodType,
    lastSuccessfulPaymentResponse?.paymentMethod
  );
  if (label) return label;

  // Resolve the raw masked card from whichever response is available
  // (scheduled payments take precedence)
  const rawMaskedCard =
    lastSuccessfulSchedulePaymentResponse?.paymentInfo?.paymentMaskedCard ??
    lastSuccessfulPaymentResponse?.paymentMaskedCard;

  if (rawMaskedCard) {
    const normalized = normalizeMaskedCard(rawMaskedCard);
    return paymentMethodType === 'bank' ? withBankPrefix(normalized) : normalized;
  }

  // Fallback: construct from cardType and the last 4 digits of any masked number
  const cardType = (
    lastSuccessfulPaymentResponse?.cardType ||
    paymentMethodType ||
    ''
  ).toUpperCase();
  const lastFour =
    (
      lastSuccessfulPaymentResponse?.maskedCardNumber ||
      lastSuccessfulPaymentResponse?.maskedAccountNumber ||
      ''
    ).slice(-4) || 'N/A';

  return `${cardType}***${lastFour}`;
};

export { ScsWalletType, ScsPaymentType };

export const getIESMaxAllowedPayAmountConfig = ({ sale, payment }) => {
  const currency = saleSelectors.currencySelector(sale);
  const currencySymbol = getCurrencySymbol(currency);

  return {
    forPaymentMethod: {
      paypal_ppaam: {
        name: 'PayPal',
        maxAllowedValue: AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT,
        maxAllowedAmount: formatNumberWithCommaAndCurrency({
          value: AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT,
          currency: currencySymbol,
        }),
      },
      venmo: {
        name: 'Venmo',
        maxAllowedValue: AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT,
        maxAllowedAmount: formatNumberWithCommaAndCurrency({
          value: AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT,
          currency: currencySymbol,
        }),
      },
      affirm_bnpl: {
        name: 'Affirm',
        maxAllowedValue: payment.affirmMaxTxnLimit,
        maxAllowedAmount: formatNumberWithCommaAndCurrency({
          value: payment.affirmMaxTxnLimit,
          currency: currencySymbol,
        }),
      },
    },
    general: {
      maxAllowedValue: AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT_CC_DC_ACH,
      maxAllowedAmount: formatNumberWithCommaAndCurrency({
        value: AMOUNT_CONFIG.MAX_ALLOWED_AMOUNT_CC_DC_ACH,
        currency: currencySymbol,
      }),
    },
  };
};
